import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { useTrackVisibility } from 'react-intersection-observer-hook';

import clientServices from '@audacy-clients/core/utils/clientServices';
import usePrevious from 'web/src/hooks/use-previous';
import { viewContext } from 'web/src/state/dataEvents';

type TTrackModuleViewProps = {
  moduleId: string;
  contentId: string | undefined;
};

/**
 * Hook to send a ModuleView Event for any component
 *
 * Note: This hook returns a `ref` that has to be tied to the component it wants to send the message for
 * @param moduleId - string
 * @param contentId - string | undefined
 */
export const useTrackModuleView = ({ moduleId, contentId }: TTrackModuleViewProps) => {
  const { viewId: viewContextViewId, contentId: viewContextContentId } =
    useRecoilValue(viewContext);
  const [moduleRef, { isVisible: isOnScreen }] = useTrackVisibility();
  const prevIsOnScreen = usePrevious(isOnScreen, false);

  useEffect(() => {
    if (!prevIsOnScreen && isOnScreen) {
      clientServices.getAnalyticServices().sendModuleView({
        viewContext: {
          viewId: viewContextViewId,
          contentId: viewContextContentId,
        },
        moduleId,
        contentId,
      });
    }
  }, [isOnScreen, moduleId, contentId, prevIsOnScreen, viewContextContentId, viewContextContentId]);

  return moduleRef;
};
